import type { DesignAsset, DesignShape } from '../../../../types/design'
import { getMaterializationAdapter } from '../../../../services/designMaterializers/index.ts'
import type { XRayAnchor, XRayDestination, XRayEdge } from '../xrayTypes'

export interface DesignResolverInput {
  shapeId?: string
  canvasId?: string
  shapes?: DesignShape[]
  asset?: DesignAsset | null
  sliceId?: string
}

export const resolveDesignAnchor = (input: DesignResolverInput): XRayAnchor => {
  const { shapeId, shapes = [], asset } = input
  const page = 'design'
  const edges: XRayEdge[] = []
  const destinations: XRayDestination[] = []

  const allShapes = shapes.length > 0 ? shapes : asset?.shapes ?? []

  // Check for intra-canvas edges (e.g., connectors)
  for (const s of allShapes) {
    if (s.type === 'CONNECTOR') {
      const conn = s as { from?: { kind?: string; shapeId?: string }; to?: { kind?: string; shapeId?: string } }
      if (
        conn.from?.kind === 'SHAPE' &&
        conn.from.shapeId &&
        conn.to?.kind === 'SHAPE' &&
        conn.to.shapeId
      ) {
        if (!shapeId || conn.from.shapeId === shapeId || conn.to.shapeId === shapeId) {
          edges.push({
            fromId: conn.from.shapeId,
            toId: conn.to.shapeId,
            kind: 'materialization',
            label: 'connects',
          })
        }
      }
    }
  }

  // Cross-canvas destinations via materialization adapter registry
  // Checklist adapter supports CHECKLIST_NODE, GATE_AND, GATE_OR
  // Form adapter supports FORM_FIELD, FORM_SECTION
  // Automation adapter supports AUTOMATION_TRIGGER, AUTOMATION_ACTION, AUTOMATION_BRANCH
  const relevantShapes = shapeId ? allShapes.filter((s) => s.id === shapeId) : allShapes

  for (const shape of relevantShapes) {
    const role = shape.semanticRole
    if (!role) continue

    const checklistAdapter = getMaterializationAdapter('CHECKLIST_BLUEPRINT')
    if (checklistAdapter?.supportedRoles.includes(role)) {
      destinations.push({
        id: shape.id,
        label: `${shape.text || 'Checklist item'} → Checklist`,
        kind: 'materialization',
        targetCanvas: 'checklist',
        targetRoute: '/checklists',
      })
    }

    const formAdapter = getMaterializationAdapter('FORM_LAYOUT')
    if (formAdapter?.supportedRoles.includes(role)) {
      destinations.push({
        id: shape.id,
        label: `${shape.text || 'Form field'} → Form`,
        kind: 'materialization',
        targetCanvas: 'form',
        targetRoute: '/forms',
      })
    }

    const autoAdapter = getMaterializationAdapter('AUTOMATION_MAP')
    if (autoAdapter?.supportedRoles.includes(role)) {
      destinations.push({
        id: shape.id,
        label: `${shape.text || 'Automation step'} → Automation`,
        kind: 'materialization',
        targetCanvas: 'automation',
        targetRoute: '/automation',
      })
    }
  }

  return {
    id: shapeId || 'canvas',
    page,
    screenPosition: null,
    edges,
    destinations,
  }
}
